<template>
  <div class="container">
    <DataGetter
      :url="`${apiRoute}/list/countryISO`"
      :params="{ indicator: 'Total' }"
      @fetched="setCountries"
    />
    <Block
      title="How is the transition to clean energy reflected in energy patents?"
    >
      <p>
        Energy patents can be classified as fossil fuel patents or clean energy
        patents<ReferenceModal :refIndex="1">
          <p>
            The European Patent Office (EPO)'s classification scheme for climate
            mitigation technologies were used to classify the patents as clean
            energy patents. A collaboration with EPO to perform text searches on
            abstracts was done to classify patents within different fossil fuel
            categories.
          </p>
          <p>
            Note that each patent can belong to more than two technology
            categories. For example, some patents related to electric vehicles
            belong to both storage and e-mobility. Therefore, the sum of patents
            from sub-categories does not equal the sum of patents from the
            corresponding parent category.
          </p> </ReferenceModal
        >, and the graph below shows trends in each type of patent.
      </p>
    </Block>
    <CountrySelect :countries="countries" v-model="selectedCountryISO" />
    <PatentsByYear :countryISO="selectedCountryISO" :apiRoute="apiRoute" />
    <AnchorBlock id="patentsByTech"
      >Number of patents by technology</AnchorBlock
    >
    <Block title="Which energy technology has the most patents?">
      <p>
        The treemap gives a broad overview how patents are distributed among
        different energy technologies.
      </p>
    </Block>
    <CountrySelect :countries="countries" v-model="selectedCountryISO">
      <template #after>
        <SliderRange
          title="Show years between"
          :min="2000"
          :max="2023"
          v-model="years"
        />
      </template>
    </CountrySelect>
    <PatentTreemap
      :countryISO="selectedCountryISO"
      groupBy="techSector"
      :apiRoute="apiRoute"
      :startYear="years[0]"
      :endYear="years[1]"
    />
    <AnchorBlock id="cleanPatents">Clean energy technology patents</AnchorBlock>
    <Block title="Which clean energy technologies have the most patents?">
      <p>
        The graph shows how patents are distributed among clean energy
        technologies, broken down into 13 categories.
      </p>
    </Block>
    <CountrySelect :countries="countries" v-model="selectedCountryISO" />
    <PatentShare
      :countryISO="selectedCountryISO"
      :apiRoute="apiRoute"
      category="Clean Energy"
      :title="`Clean energy patents by technology, ${selectedCountryName}`"
    />
    <SmallMultiples
      :title="`Clean energy patents by technology since 2000, ${selectedCountryName}`"
      :apiRoute="apiRoute"
      indicator="Detail"
      :countryISO="selectedCountryISO"
      group-dimension="techSector"
      :start-year="2000"
      :provisional-year="2022"
      :extra-params="{
        category: 'Clean energy',
      }"
      :seriesOrder="cleanEnergyOrder"
      chart-type="area"
      note="The datapoints for 2022 and 2023 are considered provisional due to the time lag in filing applications in the PATSTAT database."
    />
    <AnchorBlock id="fossilPatents">Fossil fuel technology patents</AnchorBlock>
    <Block title="Which fossil fuel technologies have the most patents?">
      <p>
        The graph shows the distribution of patents among 26 fossil fuel
        technologies, which are broken down by sector (upstream, processing and
        downstream, transmission and distribution). Note that unlike the clean
        energy transition patents, patented inventions related to fossil fuel
        demand (e.g. engines) are not included in the counts.
      </p>
    </Block>
    <CountrySelect :countries="countries" v-model="selectedCountryISO" />
    <PatentShare
      :countryISO="selectedCountryISO"
      :apiRoute="apiRoute"
      category="Fossil Fuel"
      palette=""
      :title="`Fossil fuel patents by sector, ${selectedCountryName}`"
      :seriesOrder="[
        'Upstream',
        'Processing downstream',
        'Transmission distribution',
      ]"
    />
    <ControlGroup>
      <Dropdown
        label="Sector"
        :options="[
          { label: 'Upstream', value: 'Upstream' },
          { label: 'Downstream processing', value: 'Processing downstream' },
          {
            label: 'Transmission &amp; distribution',
            value: 'Transmission distribution',
          },
        ]"
        v-model="category"
      />
    </ControlGroup>
    <SmallMultiples
      :title="`${category} fuel patents by technology since 2000, ${selectedCountryName}`"
      :apiRoute="apiRoute"
      indicator="Detail"
      :countryISO="selectedCountryISO"
      group-dimension="techSector"
      :start-year="2000"
      :provisional-year="2022"
      :extra-params="{
        category,
      }"
      :seriesOrder="null"
      chart-type="area"
      note="The datapoints for 2022 and 2023 are considered provisional due to the time lag in filing applications in the PATSTAT database."
    />
    <AnchorBlock id="fossilVsClean"
      >Country comparison of energy patents
    </AnchorBlock>
    <Block
      title="How many fossil fuel patents and clean energy patents per country? "
    >
      <p>
        The graph below shows number of fossil fuel patents and clean energy
        patents for each country. The colour of the line represents the colour
        of the largest category.
      </p>
    </Block>
    <ControlGroup>
      <SliderRange
        title="Show years between"
        :min="2000"
        :max="2023"
        v-model="years"
      />
    </ControlGroup>
    <PatentTypeComparison
      title="Number of fossil fuel vs. clean energy patents by country"
      :apiRoute="apiRoute"
      :startYear="years[0]"
      :endYear="years[1]"
    />
  </div>
</template>

<script>
import {
  AnchorBlock,
  Block,
  ControlGroup,
  Dropdown,
  SliderRange,
  DataGetter,
  ReferenceModal,
} from "@international-energy-agency/vue-ui";
import CountrySelect from "../components/CountrySelect.vue";
import PatentsByYear from "../components/PatentsByYear.vue";
import PatentTreemap from "../components/PatentTreemap.vue";
import PatentShare from "../components/PatentShare.vue";
import SmallMultiples from "../components/SmallMultiples.vue";
import PatentTypeComparison from "../components/PatentTypeComparison";

import cleanEnergyOrder from "../data/cleanEnergyOrder";

export default {
  props: {
    apiRoute: {
      type: String,
      default: "https://api.iea.org/h2020",
    },
  },
  components: {
    AnchorBlock,
    Block,
    ControlGroup,
    Dropdown,
    SliderRange,
    DataGetter,
    ReferenceModal,
    CountrySelect,
    PatentsByYear,
    PatentTreemap,
    PatentShare,
    SmallMultiples,
    PatentTypeComparison,
  },
  data() {
    return {
      countries: [],
      selectedCountryISO: "World",
      cleanEnergyOrder,
      category: "Upstream",
      years: [2000, 2023],
    };
  },
  methods: {
    setCountries(countries) {
      this.countries = countries;
    },
    getCountryName(countryISO) {
      if (countryISO === "World") return "World";
      return this.countries.find((country) => country.iso3 === countryISO).name;
    },
  },
  computed: {
    selectedCountryName() {
      return this.getCountryName(this.selectedCountryISO);
    },
  },
};
</script>
